/**
 * Coach tiers, session metrics and their storage, per docs/COACH-SPEC.md.
 * localStorage is a convenience: every read is guarded and the coach works
 * without it (tier resets to Full).
 */

export type Tier = 1 | 2 | 3;
export const TIER_NAME: Record<Tier, string> = { 1: 'Full', 2: 'Misses', 3: 'Summary' };

export interface TierState {
  tier: Tier;
  override: boolean;
  goodStreak: number;
  /** Hand numbers (within the session) that had a miss > 1.0. */
  missHands: number[];
}

export interface HandRecord {
  n: number;
  role: 'dealer' | 'pone';
  discardLoss: number;
  pegLeft: number;
  countEntered: number | null;
  countTrue: number;
  hintLevel: 0 | 1 | 2 | 3;
}

export interface SessionState {
  startedAt: string;
  hands: HandRecord[];
}

const TIER_KEY = 'cribbage.coach.tier.v1';
const SESSION_KEY = 'cribbage.coach.session.v1';

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode: the coach still works for this session */
  }
}

export function loadTier(): TierState {
  const t = read<TierState>(TIER_KEY);
  if (t && (t.tier === 1 || t.tier === 2 || t.tier === 3)) return { tier: t.tier, override: !!t.override, goodStreak: t.goodStreak | 0, missHands: Array.isArray(t.missHands) ? t.missHands : [] };
  return { tier: 1, override: false, goodStreak: 0, missHands: [] };
}
export function saveTier(t: TierState) {
  write(TIER_KEY, t);
}
/** A new game starts the streak and the miss window afresh; the tier itself persists. */
export function resetTierProgress(t: TierState): TierState {
  return { ...t, goodStreak: 0, missHands: [] };
}

export function newSession(): SessionState {
  const s = { startedAt: new Date().toISOString(), hands: [] };
  write(SESSION_KEY, s);
  return s;
}
export function saveSession(s: SessionState) {
  write(SESSION_KEY, s);
}

/** EV-loss threshold at which the pegging coach speaks. */
export function pegThreshold(tier: Tier): number {
  return tier === 1 ? 0.5 : tier === 2 ? 1.0 : Infinity;
}

/**
 * Apply one finished hand. Promote after 5 consecutive unhinted discards within
 * 0.3 of best; demote after 2 misses > 1.0 within the last 3 hands. Overrides pin.
 */
export function afterHand(t: TierState, hand: HandRecord, pegMissOver1: boolean): TierState {
  const next: TierState = { ...t, missHands: [...t.missHands] };
  const hinted = hand.hintLevel > 0;
  if (!hinted && hand.discardLoss <= 0.3) next.goodStreak += 1;
  else if (!hinted) next.goodStreak = 0;
  const missed = hand.discardLoss > 1.0 || pegMissOver1;
  if (missed) next.missHands.push(hand.n);
  next.missHands = next.missHands.filter((n) => n > hand.n - 3);
  if (!next.override) {
    if (next.goodStreak >= 5 && next.tier < 3) {
      next.tier = (next.tier + 1) as Tier;
      next.goodStreak = 0;
    } else if (next.missHands.length >= 2 && next.tier > 1) {
      next.tier = (next.tier - 1) as Tier;
      next.missHands = [];
    }
  }
  return next;
}

export interface Summary {
  hands: number;
  first: { discardLoss: number; pegLeft: number; countPct: number } | null;
  last: { discardLoss: number; pegLeft: number; countPct: number } | null;
}

function agg(h: HandRecord[]) {
  if (!h.length) return null;
  const n = h.length;
  const counted = h.filter((x) => x.countEntered !== null);
  return {
    discardLoss: h.reduce((s, x) => s + x.discardLoss, 0) / n,
    pegLeft: h.reduce((s, x) => s + x.pegLeft, 0) / n,
    countPct: counted.length ? (100 * counted.filter((x) => x.countEntered === x.countTrue).length) / counted.length : NaN,
  };
}

export function summarize(s: SessionState): Summary {
  const h = s.hands;
  return { hands: h.length, first: agg(h.slice(0, 5)), last: h.length >= 10 ? agg(h.slice(-5)) : null };
}
